import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CircularProgress } from '@mui/material';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import useDebounce from '@/operator/hooks/useDebounce';
import type { ConnectToCRMAPIInput } from '@/operator/services/graphql/types/ConnectToCRMAPIInput';
import { ConnectionSystem, useGetConnectionQuery } from 'generated/graphql';
import { isValidEmail, isValidSubdomain } from '@/operator/utils/strings';
import RequiredInput from '@/operator/components/general/input/RequiredInput';
import ErrorStep from '@/operator/components/general/multiStepForm/Error';
import {
    PageStateContext,
    PageStateDispatchContext,
} from '../../general/multiStepForm/formStateContext';
import { PageStateActions } from '../../general/multiStepForm/formStateReducer';
import {
    StyledDivButtons,
    StyledDivDescription,
    StyledDivFormDetails,
    StyledDivInputs,
    StyledLink,
} from './Styles';
import { selectPartnerData } from 'store/partner/partnerSelector';
import { useAppSelector } from 'hooks/index';

const colorTypographyGray = 'rgba(0, 0, 0, 0.60)';

const zendeskLink =
    'https://support.zendesk.com/hc/en-us/articles/4408889192858-Managing-access-to-the-Zendesk-API#:~:text=To%20generate%20an%20API%20token,be%20enabled%20in%20your%20account.&text=an%20API%20token-,In%20Admin%20Center%2C%20click%20Apps%20and%20integrations%20in%20the%20sidebar,token%20is%20generated%20and%20displayed.';
const gioargiasLink =
    'https://developers.gorgias.com/docs/access-tokens-api-keys';

interface FormAddDetailsProps {
    readonly onContinue: () => void;
    readonly onCancel: () => void;
    readonly refreshOrganizationData: () => void;
    readonly sendConnectionData: (data: ConnectToCRMAPIInput) => void;
}
function FormAddDetails({
    onContinue,
    onCancel,
    refreshOrganizationData,
    sendConnectionData,
}: FormAddDetailsProps) {
    const { t } = useTranslation();

    const [email, setAccountEmail] = useState('');
    const [subdomain, setSubdomain] = useState('');
    const [password, setPassword] = useState('');
    const [showError, setShowError] = useState<boolean>(false);
    const [hover, setHover] = useState<boolean>(false);

    const pageState = useContext(PageStateContext);
    const { instanceId } = useAppSelector(selectPartnerData);

    const {
        data: dataInitializationResult,
        loading,
        error,
    } = useGetConnectionQuery({
        variables: {
            getConnectionInput: {
                where: {
                    organizationId: instanceId,
                },
            },
        },
        fetchPolicy: 'network-only',
    });

    const pageStateDispatch = useContext(PageStateDispatchContext);

    const isSubdomain = !subdomain ? true : !isValidSubdomain(subdomain);
    const isEmail = !email ? true : isValidEmail(email);
    const debouncedInputValue = useDebounce(password, 500);

    const isButtonEnabled =
        isSubdomain && isEmail && !!(email && subdomain && password);

    useEffect(() => {
        if (dataInitializationResult?.connection) {
            pageStateDispatch?.({
                type: PageStateActions.SetFormData,
                attribute: 'connectionData',
                value: dataInitializationResult?.connection?.metadata,
            });
            // It is important for organization data to get refreshed
            // after the connection has been established so that
            // if the user cancels the flow and gets back to selecting the
            // service they'd like to add then, for this one to have its updated
            // status and prevent them from adding creds again for it.
            refreshOrganizationData();
            onContinue();
        }
    }, [dataInitializationResult]);

    useEffect(() => {
        if (error) {
            setShowError(true);
            setPassword('');
        }
    }, [error]);

    const handleContinue = async (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        event.preventDefault();

        sendConnectionData({
            system: ConnectionSystem[pageState.selectedConnector],
            subdomain,
            username: email,
            token: debouncedInputValue,
        });
        onContinue();
    };

    const arrowLabel = useMemo(() => {
        switch (pageState.selectedConnector) {
            case ConnectionSystem.Zendesk:
                return t('FormAddDetails.Inputs.Token.ArrowLabelZendesk');
            case ConnectionSystem.Gorgias:
                return t('FormAddDetails.Inputs.Token.ArrowLabelGorgias');
            default:
                return t('FormAddDetails.Inputs.Token.ArrowLabelZendesk');
        }
    }, [pageState.selectedConnector]);

    if (showError) {
        return <ErrorStep onDismiss={() => setShowError(false)} />;
    }

    return (
        <StyledDivFormDetails>
            <StyledDivDescription>
                <Typography
                    variant="h6"
                    style={{
                        color: colorTypographyGray,
                    }}
                >
                    {t('FormAddDetails.Header')}
                </Typography>
                <Typography
                    variant="body1"
                    style={{
                        color: colorTypographyGray,
                        maxWidth: '325px',
                    }}
                >
                    {t('FormAddDetails.Description', {
                        selectedConnector: pageState.selectedConnector,
                    })}
                </Typography>
                <StyledLink
                    style={{
                        marginBottom: '36px',
                    }}
                    to={
                        pageState.selectedConnector === ConnectionSystem.Zendesk
                            ? zendeskLink
                            : gioargiasLink
                    }
                    color="primary"
                    target="_blank"
                >
                    {t('FormAddDetails.LinkLabel')}
                </StyledLink>
            </StyledDivDescription>

            <StyledDivInputs>
                <>
                    <RequiredInput
                        defaultValue={subdomain || undefined}
                        suffix={`.${pageState.selectedConnector.toLowerCase()}.com`}
                        onChange={(value) => setSubdomain(value)}
                        label={
                            t('FormAddDetails.Inputs.Url.Label', {
                                selectedConnector: pageState.selectedConnector,
                            }) as string
                        }
                        helperText={
                            t('FormAddDetails.Inputs.Url.SubLabel', {
                                selectedConnector: pageState.selectedConnector,
                            }) as string
                        }
                        errorLabel={
                            isSubdomain
                                ? !subdomain && hover
                                    ? (t(
                                          'FormAddDetails.Inputs.Url.ErrorLabel',
                                      ) as string)
                                    : undefined
                                : (t(
                                      'FormAddDetails.Inputs.Url.ErrorLabel',
                                  ) as string)
                        }
                    />
                    <RequiredInput
                        defaultValue={email || undefined}
                        onChange={(value) => setAccountEmail(value)}
                        label={t('FormAddDetails.Inputs.Email.Label') as string}
                        helperText={
                            t('FormAddDetails.Inputs.Email.SubLabel', {
                                selectedConnector: pageState.selectedConnector,
                            }) as string
                        }
                        errorLabel={
                            isEmail
                                ? !email && hover
                                    ? (t(
                                          'FormAddDetails.Inputs.Email.ErrorLabel',
                                      ) as string)
                                    : undefined
                                : (t(
                                      'FormAddDetails.Inputs.Email.ErrorLabel',
                                  ) as string)
                        }
                    />
                    <RequiredInput
                        type="password"
                        label={t('FormAddDetails.Inputs.Token.Label') as string}
                        onChange={(value) => setPassword(value)}
                        helperText={
                            (t('FormAddDetails.Inputs.Token.SubLabel', {
                                selectedConnector: pageState.selectedConnector,
                            }) + arrowLabel) as string
                        }
                        errorLabel={
                            !password && hover
                                ? (t(
                                      'FormAddDetails.Inputs.Token.ErrorLabel',
                                  ) as string)
                                : undefined
                        }
                    />
                </>
                <StyledDivButtons
                    onMouseEnter={() => {
                        if (!hover) {
                            setHover(true);
                        }
                    }}
                >
                    <Button variant="text" size="small" onClick={onCancel}>
                        {t('FormAddDetails.CancelButton')}
                    </Button>
                    <Button
                        color="primary"
                        size="small"
                        variant={loading ? 'text' : 'contained'}
                        style={{
                            justifyContent: 'center',
                        }}
                        disabled={loading ? true : !isButtonEnabled}
                        onClick={handleContinue}
                    >
                        {loading
                            ? t('FormAddDetails.ConnectLoadingButton', {
                                  selectedConnector:
                                      pageState.selectedConnector,
                              })
                            : t('FormAddDetails.ConnectButton', {
                                  selectedConnector:
                                      pageState.selectedConnector,
                              })}
                        {loading && (
                            <CircularProgress
                                variant="indeterminate"
                                size="0.75rem"
                                sx={{
                                    marginLeft: '8px',
                                }}
                            />
                        )}
                    </Button>
                </StyledDivButtons>
            </StyledDivInputs>
        </StyledDivFormDetails>
    );
}

export default FormAddDetails;
